import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../api/api';
import { KPI_WEIGHTS, KPI_COLUMNS, defaultDateRange } from '../utils/vendorKpi';
import { StatusIcon } from '../components/vendorMgmt/VendorKpiUi';

const PIE_COLORS = ['#4A9AF5', '#7B61FF', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

const FINANCIAL_RISK_STYLES = {
  Low: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-800 border-amber-200',
  High: 'bg-red-50 text-red-800 border-red-200',
};

function MetricBox({ title, value, subtitle, badge }) {
  return (
    <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4 min-h-[130px] flex flex-col">
      <h3 className="text-xs font-semibold text-sap-text mb-2">{title}</h3>
      <div className="text-2xl font-light text-sap-text flex items-center gap-2 flex-wrap">
        {badge ? (
          <span className={`inline-flex px-2.5 py-1 rounded-full text-sm font-semibold border ${FINANCIAL_RISK_STYLES[badge] || FINANCIAL_RISK_STYLES.Low}`}>
            {badge}
          </span>
        ) : (
          value
        )}
      </div>
      {subtitle && <p className="text-[10px] text-sap-textLight mt-auto pt-2">{subtitle}</p>}
    </div>
  );
}

function ScorePieChart({ segments, totalScore }) {
  const size = 200;
  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const sum = segments.reduce((s, x) => s + x.value, 0) || 1;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E8EDF2" strokeWidth="18" />
          {segments.map((seg, i) => {
            const len = (seg.value / sum) * circ;
            const dash = `${len} ${circ - len}`;
            const el = (
              <circle
                key={seg.key}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={PIE_COLORS[i % PIE_COLORS.length]}
                strokeWidth="18"
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-3xl font-bold text-sap-text">{Number(totalScore || 0).toFixed(1)}</span>
          <span className="text-[10px] text-sap-textLight uppercase tracking-wide">Total score</span>
        </div>
      </div>
      <ul className="flex-1 space-y-2 min-w-0">
        {segments.map((seg, i) => (
          <li key={seg.key} className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
            <span className="flex-1 truncate text-sap-text">{seg.label}</span>
            <span className="text-sap-textLight">{seg.weight}% wt</span>
            <span className="font-semibold text-sap-text w-14 text-right">{seg.displayValue}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function kpiCellBg(value, isPct) {
  if (!isPct) return '';
  if (value >= 80) return 'bg-emerald-50 text-emerald-800';
  if (value >= 60) return 'bg-amber-50 text-amber-800';
  return 'bg-red-50 text-red-800';
}

export default function VendorMgmtDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const defaults = defaultDateRange();
  const [startDate, setStartDate] = useState(defaults.start_date);
  const [endDate, setEndDate] = useState(defaults.end_date);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('performance');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await api.get(`/vendors/${id}/performance-detail?${qs}`);
      setData(res);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, startDate, endDate]);

  useEffect(() => {
    dispatch({ type: 'SET_BREADCRUMB', payload: ['Home', 'Vendor Management', data?.vendor?.vendor_name || 'Vendor'] });
  }, [dispatch, data?.vendor?.vendor_name]);

  useEffect(() => { load(); }, [load]);

  const vendor = data?.vendor;
  const metrics = data?.metrics || {};
  const overview = data?.overview || {};
  const risk = data?.risk;

  const weightedPieSegments = useMemo(() => {
    return KPI_COLUMNS.filter((col) => (KPI_WEIGHTS[col.key] || 0) > 0).map((col) => {
      const val = Number(metrics[col.key]) || 0;
      const weight = KPI_WEIGHTS[col.key] || 0;
      return {
        key: col.key,
        label: col.label,
        weight,
        value: val * weight,
        displayValue: col.isPct !== false ? `${val.toFixed(1)}%` : String(val),
      };
    });
  }, [metrics]);

  if (loading && !data) {
    return <div className="py-16 text-center text-sm text-sap-textLight">Loading vendor…</div>;
  }
  if (!vendor) {
    return <div className="py-16 text-center text-sm text-red-600">Vendor not found</div>;
  }

  const financialLevel = metrics.financial_risk_level || 'Low';
  const financialValue = metrics.financial_risk_value ?? 0;

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-sap-text flex items-center gap-2 flex-wrap">
            {vendor.vendor_name}
            <StatusIcon value={metrics.vendor_score} />
          </h1>
          <div className="flex flex-wrap gap-4 mt-2 text-xs text-sap-textLight">
            <span><strong className="text-sap-text">Vendor score:</strong> {Number(metrics.vendor_score || 0).toFixed(2)}</span>
            <span><strong className="text-sap-text">Risk level:</strong> {risk?.risk_level || metrics.rank_tier || '—'}</span>
            {risk?.final_risk_score != null && (
              <span><strong className="text-sap-text">Risk score:</strong> {Number(risk.final_risk_score).toFixed(2)}</span>
            )}
            <span><strong className="text-sap-text">Rank tier:</strong> {metrics.rank_tier}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" />
          <button type="button" onClick={() => navigate('/vendor-management')} className="px-3 py-1.5 text-xs border rounded-lg bg-white hover:bg-sap-bg">
            Back to workspace
          </button>
          <button type="button" onClick={() => navigate(`/vendor/${id}`)} className="px-3 py-1.5 text-xs rounded-lg bg-sap-blue text-white hover:opacity-90">
            Master data
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-sap-border/60 overflow-x-auto">
        {['performance', 'details'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-semibold border-b-2 whitespace-nowrap capitalize ${
              tab === t ? 'border-sap-blue text-sap-blue' : 'border-transparent text-sap-textLight'
            }`}
          >
            {t === 'performance' ? 'Performance' : 'Details'}
          </button>
        ))}
      </div>

      {tab === 'performance' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricBox
              title="Financial Risk"
              badge={financialLevel}
              subtitle={`Anomaly indicator: ${financialValue} (${financialLevel === 'Low' ? '0 = Low' : financialValue < 0 ? 'Negative = High' : 'Positive = Medium'})`}
            />
            <MetricBox
              title="Procurement Failure"
              value={`${Number(metrics.procurement_failure_pct || 0).toFixed(1)}%`}
              subtitle="Placeholder until procurement failure feed is connected"
            />
            <MetricBox
              title="Delivery Performance"
              value={`${Number(metrics.delivery_performance_pct || 0).toFixed(1)}%`}
              subtitle="Placeholder until delivery KPI feed is connected"
            />
            <MetricBox
              title="Rework Order"
              value={`${Number(metrics.rework_order_pct || 0).toFixed(1)}%`}
              subtitle="Placeholder until rework KPI feed is connected"
            />
            <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm overflow-hidden min-h-[130px] sm:col-span-2 lg:col-span-1">
              <div className="px-4 py-2 border-b border-sap-border/40 bg-[#F7F5F2]">
                <h3 className="text-xs font-semibold text-sap-text">KPIs</h3>
              </div>
              <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase text-sap-textLight">
                      <th className="px-3 py-1.5 text-left font-semibold">KPI</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Wt</th>
                      <th className="px-3 py-1.5 text-right font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {KPI_COLUMNS.map((kpi) => (
                      <tr key={kpi.key} className="border-t border-sap-border/30">
                        <td className="px-3 py-1.5">{kpi.label}</td>
                        <td className="px-3 py-1.5 text-right text-sap-textLight">
                          {KPI_WEIGHTS[kpi.key] ? `${KPI_WEIGHTS[kpi.key]}%` : '—'}
                        </td>
                        <td className={`px-3 py-1.5 text-right font-medium ${kpiCellBg(metrics[kpi.key], kpi.isPct !== false)}`}>
                          {kpi.isPct === false
                            ? (metrics[kpi.key] ?? '—')
                            : `${Number(metrics[kpi.key] || 0).toFixed(2)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-sap-text mb-4">Score breakdown</h3>
            <ScorePieChart segments={weightedPieSegments} totalScore={metrics.vendor_score} />
            <p className="text-[10px] text-sap-textLight mt-4">
              Weighted KPI contributions (25% each). Total score uses ML risk data when available; otherwise operational KPIs.
            </p>
          </div>

          <aside className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-sap-text mb-3">Overview</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <dt className="text-[11px] text-sap-textLight">Website</dt>
                <dd>{overview.website ? <a href={overview.website.startsWith('http') ? overview.website : `https://${overview.website}`} className="text-sap-blue hover:underline" target="_blank" rel="noreferrer">{overview.website}</a> : '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Relationship start</dt>
                <dd>{overview.relationship_start || '—'}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Vendor type</dt>
                <dd>{overview.vendor_type}</dd>
              </div>
              <div>
                <dt className="text-[11px] text-sap-textLight">Vendor manager</dt>
                <dd>{overview.vendor_manager}</dd>
              </div>
            </dl>
          </aside>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-sap-border/60 shadow-sm p-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><dt className="text-[11px] text-sap-textLight">Vendor code</dt><dd className="font-medium">{vendor.vendor_code}</dd></div>
            <div><dt className="text-[11px] text-sap-textLight">Payment terms</dt><dd>{vendor.payment_terms || '—'}</dd></div>
            <div><dt className="text-[11px] text-sap-textLight">Email</dt><dd>{vendor.email || '—'}</dd></div>
            <div><dt className="text-[11px] text-sap-textLight">Industry</dt><dd>{vendor.industry || '—'}</dd></div>
            <div className="sm:col-span-2"><dt className="text-[11px] text-sap-textLight">Remarks</dt><dd>{vendor.remarks || '—'}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}
